/**
 * Leaflet.PolylineOffset — ESM port for Leaflet 2.0
 * Original: https://github.com/bbecquet/Leaflet.PolylineOffset
 * Adds lateral pixel offset support to Polyline layers.
 */

import { Polyline, LatLng, LineUtil, Point } from 'leaflet';

function forEachPair(list, callback) {
  if (!list || list.length < 1) return;
  for (let i = 1; i < list.length; i++) {
    callback(list[i - 1], list[i]);
  }
}

function lineEquation(pt1, pt2) {
  if (pt1.x === pt2.x) {
    return pt1.y === pt2.y ? null : { x: pt1.x };
  }
  const a = (pt2.y - pt1.y) / (pt2.x - pt1.x);
  return { a, b: pt1.y - a * pt1.x };
}

function intersection(l1a, l1b, l2a, l2b) {
  const line1 = lineEquation(l1a, l1b);
  const line2 = lineEquation(l2a, l2b);

  if (line1 === null || line2 === null) return null;

  if ('x' in line1) {
    return 'x' in line2
      ? null
      : { x: line1.x, y: line2.a * line1.x + line2.b };
  }
  if ('x' in line2) {
    return { x: line2.x, y: line1.a * line2.x + line1.b };
  }
  if (line1.a === line2.a) return null;

  const x = (line2.b - line1.b) / (line1.a - line2.a);
  return { x, y: line1.a * x + line1.b };
}

function translatePoint(pt, dist, heading) {
  return {
    x: pt.x + dist * Math.cos(heading),
    y: pt.y + dist * Math.sin(heading),
  };
}

const PolylineOffset = {
  offsetPointLine(points, distance) {
    const offsetSegments = [];

    forEachPair(points, (a, b) => {
      if (a.x === b.x && a.y === b.y) return;

      const segmentAngle = Math.atan2(a.y - b.y, a.x - b.x);
      const offsetAngle = segmentAngle - Math.PI / 2;

      offsetSegments.push({
        offsetAngle,
        original: [a, b],
        offset: [
          translatePoint(a, distance, offsetAngle),
          translatePoint(b, distance, offsetAngle),
        ],
      });
    });

    return offsetSegments;
  },

  offsetPoints(pts, options) {
    const offsetSegments = this.offsetPointLine(
      LineUtil.simplify(pts, options.smoothFactor),
      options.offset,
    );
    return this.joinLineSegments(offsetSegments, options.offset);
  },

  joinSegments(s1, s2, offset) {
    return this.circularArc(s1, s2, offset).filter((x) => x);
  },

  joinLineSegments(segments, offset) {
    let joinedPoints = [];
    const first = segments[0];
    const last = segments[segments.length - 1];

    if (first && last) {
      joinedPoints.push(first.offset[0]);
      forEachPair(segments, (s1, s2) => {
        joinedPoints = joinedPoints.concat(this.joinSegments(s1, s2, offset));
      });
      joinedPoints.push(last.offset[1]);
    }

    return joinedPoints;
  },

  segmentAsVector(s) {
    return { x: s[1].x - s[0].x, y: s[1].y - s[0].y };
  },

  getSignedAngle(s1, s2) {
    const a = this.segmentAsVector(s1);
    const b = this.segmentAsVector(s2);
    return Math.atan2(a.x * b.y - a.y * b.x, a.x * b.x + a.y * b.y);
  },

  circularArc(s1, s2, distance) {
    if (s1.offsetAngle === s2.offsetAngle) {
      return [s1.offset[1]];
    }

    const signedAngle = this.getSignedAngle(s1.offset, s2.offset);
    if (
      signedAngle * distance > 0 &&
      signedAngle * this.getSignedAngle(s1.offset, [s1.offset[0], s2.offset[1]]) > 0
    ) {
      return [intersection(s1.offset[0], s1.offset[1], s2.offset[0], s2.offset[1])];
    }

    const points = [];
    const center = s1.original[1];
    const rightOffset = distance > 0;
    const startAngle = rightOffset ? s2.offsetAngle : s1.offsetAngle;
    let endAngle = rightOffset ? s1.offsetAngle : s2.offsetAngle;
    if (endAngle < startAngle) endAngle += Math.PI * 2;

    const step = Math.PI / 8;
    for (let alpha = startAngle; alpha < endAngle; alpha += step) {
      points.push(translatePoint(center, distance, alpha));
    }
    points.push(translatePoint(center, distance, endAngle));

    return rightOffset ? points.reverse() : points;
  },
};

// Modify L.Polyline by overwriting the projection function
Polyline.include({
  _projectLatlngs(latlngs, result, projectedBounds) {
    const isFlat = latlngs.length > 0 && latlngs[0] instanceof LatLng;

    if (isFlat) {
      let ring = latlngs.map((ll) => {
        const point = this._map.latLngToLayerPoint(ll);
        if (projectedBounds) projectedBounds.extend(point);
        return point;
      });

      if (this.options.offset) {
        ring = PolylineOffset.offsetPoints(ring, this.options);
      }

      result.push(ring.map((xy) => new Point(xy.x, xy.y)));
    } else {
      latlngs.forEach((ll) => {
        this._projectLatlngs(ll, result, projectedBounds);
      });
    }
  },
});

Polyline.include({
  setOffset(offset) {
    this.options.offset = offset;
    this.redraw();
    return this;
  },
});

export { PolylineOffset };
